import React from 'react';
import {
  Col, Row, Card,
} from 'react-bootstrap';
import Button from 'components/DesignSystem/Button';
import useAaoNaoOptions from 'hooks/Meteorology/Weather/Area/Status/AAONAO/useAaoNaoOptions';
import useStatusImageDownload from 'hooks/Meteorology/Weather/Area/Status/useStatusImageDownload';
import usePredictionImageDownload from 'hooks/Meteorology/Weather/Area/Status/usePredictionImageDownload';
import useAaoNaoSelection from 'hooks/Meteorology/Weather/Area/Status/AAONAO/useAaoNaoSelection';
import CentralizedSpinner from 'components/CentralizedSpinner';
import EnlargeableImage from 'components/Meteorology/Fields/SynopticCharts/SynopticChart/Map/EnlargeableImage';
import Selector from 'components/DesignSystem/Selector';
import useIsMobile from 'utils/hooks/useIsMobile';
import StatusCard from 'components/Meteorology/Weather/Area/Status/Mobile/StatusCard';

export default function AAONAO({ area }: { area: 'aao' | 'nao' }) {
  const isMobile = useIsMobile();
  const { data, isLoading: optionsLoading } = useAaoNaoOptions(area);
  const {
    model,
    handleModelChange,
    period,
    handlePeriodChange,
    statusImageBase64,
    statusImageLoading,
    predictionImageBase64,
    predictionImageLoading,
  } = useAaoNaoSelection(area);
  const {
    onStatusDownloadClick,
    downloadDisabled: statusDownloadDisabled,
  } = useStatusImageDownload(statusImageBase64, statusImageLoading, area);
  const {
    onPredictionDownloadClick,
    downloadDisabled: predictionDownloadDisabled,
  } = usePredictionImageDownload(
    predictionImageBase64,
    predictionImageLoading,
    area,
    model,
    period,
  );

  const periodSelector = (
    <Selector
      id="periodSelect"
      placeholder="Período"
      className={isMobile ? '' : 'ms-lg-2'}
      value={period}
      setValue={handlePeriodChange}
      containerClassName={isMobile ? 'flex-grow-1' : 'flex-grow-1 me-2'}
    >
      {Object.entries(data?.periods || {}).map(([key, value]) => (
        <option key={key} value={key}>{value}</option>
      ))}
    </Selector>
  );

  const modelSelector = (
    <Selector
      id="modelSelect"
      placeholder="Modelo"
      value={model}
      setValue={handleModelChange}
      containerClassName={isMobile ? 'flex-grow-1' : 'flex-grow-1 me-2'}
    >
      {data?.models.map((option) => (
        <option key={option} value={option}>{option}</option>
      ))}
    </Selector>
  );

  const statusDownload = (
    <Button
      id="statusImageDownloadButton"
      small
      className={`icon-button weather-download-button${isMobile ? '' : ' ms-lg-3'}`}
      onClick={onStatusDownloadClick}
      disabled={statusDownloadDisabled}
      icon="download"
    />
  );

  const predictionDownload = (
    <Button
      id="previewImageDownloadButton"
      small
      className={`icon-button weather-download-button${isMobile ? '' : ' ms-lg-3'}`}
      onClick={onPredictionDownloadClick}
      disabled={predictionDownloadDisabled}
      icon="download"
    />
  );

  const statusImage = !statusImageLoading && statusImageBase64 && (
    <EnlargeableImage src={`data:image/png;base64,${statusImageBase64}`} className="mt-3" />
  );

  const predictionImage = !predictionImageLoading && predictionImageBase64 && (
    <EnlargeableImage src={`data:image/png;base64,${predictionImageBase64}`} className="mt-3" />
  );

  if (isMobile) {
    return (
      <div className="status-stack-mobile">
        <StatusCard title="OBSERVADO">
          {optionsLoading ? <CentralizedSpinner /> : (
            <>
              <div className="status-controls-mobile">
                {periodSelector}
                {statusDownload}
              </div>
              {statusImageLoading && <CentralizedSpinner />}
              {statusImage}
            </>
          )}
        </StatusCard>
        <StatusCard title="FONTE">
          {optionsLoading ? <CentralizedSpinner /> : (
            <>
              <div className="status-controls-mobile">
                {modelSelector}
                {predictionDownload}
              </div>
              {predictionImageLoading && <CentralizedSpinner />}
              {predictionImage}
            </>
          )}
        </StatusCard>
      </div>
    );
  }

  return (
    <Row className="flex-fill p-5 h-100">
      <Col xl={6}>
        <Card className="shadow-sm forecast-instruction-card h-100">
          <Card.Header>
            OBSERVADO
          </Card.Header>
          <Card.Body>
            {optionsLoading ? (
              <CentralizedSpinner />
            ) : (
              <>
                <div className="d-lg-flex mt-3 mx-3 align-items-center">
                  {periodSelector}
                  {statusDownload}
                </div>
                {statusImageLoading && <CentralizedSpinner />}
                {statusImage}
              </>
            )}
          </Card.Body>
        </Card>
      </Col>
      <Col xl={6} className="mt-3 mt-xl-0">
        <Card className="shadow-sm forecast-instruction-card h-100">
          <Card.Header>
            FONTE
          </Card.Header>
          <Card.Body>
            {optionsLoading ? (
              <CentralizedSpinner />
            ) : (
              <>
                <div className="d-lg-flex mt-3 mx-3 align-items-center">
                  {modelSelector}
                  {predictionDownload}
                </div>
                {predictionImageLoading && <CentralizedSpinner />}
                {predictionImage}
              </>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}
